import React from 'react'
import { Modal } from '@ztechapps/webui.modal'
import { Stack } from 'react-bootstrap'
import { Alert, HorizontalStack, ShowIf } from '@ztechapps/webui.util'
import { SaveCancelButtons } from '@ztechapps/webui.button'
import { useMetadataContext } from './Context'
import { MetadataInput } from './MetadataInput'
import { MaximizeButton } from './MaximizeButton'
import styles from './SpreadsheetMaximizeModal.module.scss'

/**
 * Near-fullscreen modal that hosts a copy of the Specifications grid for bulk-entry workflows.
 *
 * Sizing mirrors the PAPI ChatModal pattern at
 * `libs/ai/webui/src/floating-chat-bubble/FloatingChatBubble.module.scss:142-171` (copied because the
 * PAPI block bundles corner anchoring with sizing; this modal sits inside a centered react-bootstrap
 * dialog so only the size pieces transfer).
 *
 * Modal Esc-to-close is DISABLED (`keyboard={false}`). Esc is owned by the spreadsheet's per-cell
 * edit-mode revert (see `useSpreadsheetNavigation` → "exitEditMode"). Without this gate, pressing
 * Esc inside an open react-select menu would bubble up to the dialog and close the entire modal
 * mid-edit. Save / Cancel are the explicit exits — both close the modal AND return to view mode.
 *
 * The Restore button (the `MaximizeButton` in the header) closes the modal only — the user lands
 * back on the inline edit grid.
 */
export const SpreadsheetMaximizeModal = () => {
  const {
    isMaximized,
    toggleMaximized,
    isChange,
    onSave,
    onRevert,
    onUpdateLoading,
    onUpdateError,
    headerName,
    setShowEdit,
  } = useMetadataContext()

  const onSaveAndClose = async () => {
    try {
      await onSave()
      setShowEdit(true)
      toggleMaximized()
    } catch {
      // Stay open on error; the <Alert> below renders the error.
    }
  }

  const onCancelAndClose = () => {
    onRevert()
    setShowEdit(true)
    toggleMaximized()
  }

  return (
    <Modal
      show={isMaximized}
      onHide={toggleMaximized}
      backdrop={'static'}
      keyboard={false}
      size={'90'}
      dialogClassName={styles.dialogDesktop}
      resizable
      resizeStorageKey="spec-grid-maximize"
      trigger={() => null}
    >
      <Modal.Header>
        <HorizontalStack className={'justify-content-between align-items-center w-100'}>
          <HorizontalStack gap={2}>
            <h4 className="m-0 text-body">{headerName}</h4>
            <MaximizeButton />
          </HorizontalStack>
          <SaveCancelButtons
            onSave={onSaveAndClose}
            onCancel={onCancelAndClose}
            isBusy={onUpdateLoading}
            disabled={!isChange}
          />
        </HorizontalStack>
      </Modal.Header>

      <Modal.Body className={styles.body}>
        <Stack gap={2}>
          <ShowIf condition={!!onUpdateError}>
            <Alert type="danger" icon="exclamation-triangle">
              <span>{onUpdateError?.message ?? 'Failed to save specifications'}</span>
            </Alert>
          </ShowIf>

          {/* Second `<MetadataInput />` shares `MetadataContext` with the inline copy in `Layout`,
              so `list`, `isChange`, and `activeCell` track in lockstep. When the modal closes,
              react-bootstrap unmounts the dialog tree and this copy unmounts with it. */}
          <MetadataInput />
        </Stack>
      </Modal.Body>
    </Modal>
  )
}
